import { useCategorizeDiff } from '@common/git/git-hooks';
import { useVersionState, simplifyVersionDeploymentStatus } from '@common/git/Project/context';
import { useProjectState } from '@common/git/Project/hooks';
import { Version } from '@common/git/Project/types';
import { GitQueryKeys } from '@common/queries/GitQueryKeys';
import { renderColumnWithEllipsis } from '@common/utils/renderUtils';
import { showDeploymentLogs } from '@pages/Sql/VersionBrowser/DeploymentLogs';
import { DiffContainer } from '@pages/Sql/VersionBrowser/DiffSection';
import { LoadingDIcon, RefreshCwEIcon, LoadingBIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Badges, Button, Dropdown, Ellipsis, MultilineEllipsis, Stack, Table, Text } from '@prophecy/ui-v3';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import styled from 'styled-components';
import { format as timeAgoFormat } from 'timeago.js';

const VersionBottomLine = styled.div<{ noVisibility: boolean }>`
  height: 100%;
  background-color: ${theme.colors.gray200};
  width: 2px;
  margin-top: ${theme.spaces.x4};
  visibility: ${({ noVisibility }) => (noVisibility ? 'hidden' : 'visible')};
`;

const VersionTopLine = styled.div`
  height: ${theme.spaces.x12};
  background-color: ${theme.colors.gray200};
  width: 2px;
`;

const VersionPill = styled(Text)<{ type: 'primary' | 'secondary' }>`
  display: inline-block;
  background-color: ${({ type }) => (type === 'primary' ? theme.colors.blueLight50 : theme.colors.gray100)};
  border: 1px solid ${({ type }) => (type === 'primary' ? theme.colors.blueLight200 : theme.colors.gray200)};
  color: ${({ type }) => (type === 'primary' ? theme.colors.blueLight600 : theme.colors.gray700)} !important;
  padding: ${theme.spaces.x2} ${theme.spaces.x4} ${theme.spaces.x2} ${theme.spaces.x8};
  border-radius: ${theme.radius.x32};
  max-width: 40px;
  width: fit-content;

  font-weight: ${theme.fontWeight.normal};
`;

export const VersionHistoryPage = () => {
  const { data: version, isInitialLoading } = useVersionState();

  const AllVersions = styled(Stack)`
    margin-top: ${theme.spaces.x24};
  `;

  const VersionDetailContent = styled(Stack)`
    padding: ${theme.spaces.x16};
    width: 100%;
    gap: ${theme.spaces.x4};
    cursor: pointer;
    border-radius: ${theme.radius.x8};
    &:hover {
      background-color: ${theme.colors.gray100};
    }
  `;

  const StyledDropdownContent = styled(Stack)`
    padding: ${theme.spaces.x8};
  `;

  const VersionDetailContainer = styled(Stack)`
    gap: ${theme.spaces.x16};
  `;

  const [expandedVersionsIndex, setExpandedVersionsIndex] = useState<number[]>([]);

  const toggleVersion = (versionIndex: number) => {
    if (expandedVersionsIndex.includes(versionIndex)) {
      setExpandedVersionsIndex(expandedVersionsIndex.filter((v) => v !== versionIndex));
    } else {
      setExpandedVersionsIndex([...expandedVersionsIndex, versionIndex]);
    }
  };

  const VersionDetail = ({
    version,
    isFirst,
    versionsList,
    versionIndex,
    toggleVersion,
    lastVersion
  }: {
    version: Version;
    isFirst: boolean;
    versionsList: Version[];
    versionIndex: number;
    toggleVersion: (versionIndex: number) => void;
    lastVersion: boolean;
  }) => {
    const { projectId, currentBranch } = useProjectState();

    const { updateDiffState, isLoadingDiff, separatedDiff, setSeparatedDiff } = useCategorizeDiff(
      projectId,
      currentBranch
    );

    return (
      <VersionDetailContainer direction='horizontal'>
        <Stack direction='vertical' style={{ justifyContent: 'center', alignItems: 'center', width: theme.spaces.x28 }}>
          {isFirst ? null : <VersionTopLine />}
          <VersionPill
            style={{ height: 'fit-content', marginTop: isFirst ? theme.spaces.x16 : theme.spaces.x4 }}
            level='xs'
            type={isFirst ? 'primary' : 'secondary'}>
            <Ellipsis tooltip>{version.version}</Ellipsis>
          </VersionPill>

          <VersionBottomLine noVisibility={lastVersion} />
        </Stack>
        <VersionDetailContent direction='vertical' onClick={() => toggleVersion(versionIndex)}>
          <Stack direction='horizontal' align='space-between'>
            <Stack gap={theme.spaces.x4}>
              <Text level='xs' tone={theme.colors.gray500} weight={theme.fontWeight.normal}>
                {version.created ? timeAgoFormat(version.created) : null}
              </Text>
              <Text
                style={{ marginTop: theme.spaces.x4 }}
                level='md'
                tone={theme.colors.gray700}
                weight={theme.fontWeight.medium}>
                Version {version.version}
              </Text>
            </Stack>
            <Button
              variant='secondaryGray'
              size='s'
              tooltip={
                version?.fabrics?.length === 0 ? `Version ${version.version} is not published to any fabric` : undefined
              }
              disabled={version?.fabrics?.length === 0}
              onClick={(e) => {
                e.stopPropagation();
                showDeploymentLogs(projectId, version.lastDeploymentId || '', version.version || '');
              }}
              data-test-id='version-history-page-logs-button'>
              Logs
            </Button>
          </Stack>
          <Text level='xs' tone={theme.colors.gray500} weight={theme.fontWeight.normal}>
            <MultilineEllipsis expandable lines={2} ellipsis={version.tagMessage}>
              {version.tagMessage || 'No description'}
            </MultilineEllipsis>
          </Text>
          <Stack
            direction='horizontal'
            style={{ justifyContent: 'space-between', width: '100%', marginTop: theme.spaces.x8 }}
            gap={theme.spaces.x4}>
            <Dropdown
              onVisibleChange={async (visible) => {
                if (visible) {
                  const secondCommit =
                    versionsList[versionIndex + 1]?.commitHash ||
                    version.commits[version.commits.length - 1]?.commitHash;

                  updateDiffState(version.commits[0]?.commitHash || version.commitHash || '', secondCommit || '');
                } else {
                  setSeparatedDiff({ created: [], modified: [], deleted: [], files: [] });
                }
              }}
              data-test-id='version-diff-dropdown'
              overlay={
                <StyledDropdownContent direction='vertical'>
                  {isLoadingDiff ? (
                    <>
                      <LoadingDIcon type='extended' spin={true} />
                    </>
                  ) : (
                    <DiffContainer separatedDiff={separatedDiff} />
                  )}
                </StyledDropdownContent>
              }>
              <Text
                level='xs'
                tone={theme.colors.gray600}
                style={{ cursor: 'pointer', borderBottom: `1px dotted ${theme.colors.gray600}` }}
                weight={theme.fontWeight.normal}>
                View changes
              </Text>
            </Dropdown>

            <Text level='xs' tone={theme.colors.gray500} weight={theme.fontWeight.normal}>
              Published by {version.authorName}
            </Text>
          </Stack>
        </VersionDetailContent>
      </VersionDetailContainer>
    );
  };

  const CommitDetail = ({
    commit,
    isLastCommit,
    isInitialCommit,
    publishedVersions,
    versionIndex,
    currentCommitIndex
  }: {
    commit: {
      commitHash: string;
      message: string;
      authorName: string;
      created: number;
    };
    isInitialCommit: boolean;
    isLastCommit: boolean;
    publishedVersions: Version[];
    versionIndex: number;
    currentCommitIndex: number;
  }) => {
    const { projectId, currentBranch } = useProjectState();

    const { updateDiffState, isLoadingDiff, separatedDiff, setSeparatedDiff } = useCategorizeDiff(
      projectId,
      currentBranch
    );

    return (
      <VersionDetailContainer direction='horizontal'>
        <Stack direction='vertical' style={{ justifyContent: 'center', alignItems: 'center', width: theme.spaces.x28 }}>
          <div
            style={{
              height: '100%',
              backgroundColor: theme.colors.gray200,
              width: '2px',
              marginBottom: theme.spaces.x2
            }}
          />
          <div
            style={{
              height: theme.spaces.x28,
              width: theme.spaces.x12,
              backgroundColor: theme.colors.gray200,
              borderRadius: '50%'
            }}
          />
          <div
            style={{
              height: '100%',
              backgroundColor: theme.colors.gray200,
              width: '2px',
              marginTop: theme.spaces.x2,
              visibility: isInitialCommit ? 'hidden' : 'visible'
            }}
          />
        </Stack>
        <VersionDetailContent direction='vertical'>
          <Text level='xs' tone={theme.colors.gray500} weight={theme.fontWeight.normal}>
            {timeAgoFormat(commit.created)}
          </Text>
          <Text
            style={{ marginTop: theme.spaces.x4 }}
            level='md'
            tone={theme.colors.gray700}
            weight={theme.fontWeight.medium}>
            {commit.message}
          </Text>

          <Stack
            direction='horizontal'
            style={{ justifyContent: 'space-between', width: '100%', marginTop: theme.spaces.x8 }}
            gap={theme.spaces.x4}>
            <Dropdown
              onVisibleChange={async (visible) => {
                if (visible) {
                  const isCurrentCommitLast = isLastCommit;
                  const previousVersionCommit =
                    publishedVersions[versionIndex + 1]?.commits[0]?.commitHash ||
                    publishedVersions[versionIndex + 1].commitHash ||
                    '';
                  if (isCurrentCommitLast && !previousVersionCommit) {
                    return;
                  }
                  const secondCommit = isCurrentCommitLast
                    ? previousVersionCommit
                    : publishedVersions[versionIndex].commits[currentCommitIndex + 1]?.commitHash || '';

                  updateDiffState(commit.commitHash || '', secondCommit);
                } else {
                  setSeparatedDiff({ created: [], modified: [], deleted: [], files: [] });
                }
              }}
              data-test-id='commit-diff-dropdown'
              overlay={
                <StyledDropdownContent direction='vertical'>
                  {isLoadingDiff ? (
                    <>
                      <LoadingDIcon type='extended' spin={true} />
                    </>
                  ) : (
                    <DiffContainer separatedDiff={separatedDiff} />
                  )}
                </StyledDropdownContent>
              }>
              <Text
                level='xs'
                tone={theme.colors.gray600}
                style={{ cursor: 'pointer', borderBottom: `1px dotted ${theme.colors.gray600}` }}
                weight={theme.fontWeight.normal}>
                View changes
              </Text>
            </Dropdown>

            <Text level='xs' tone={theme.colors.gray500} weight={theme.fontWeight.normal}>
              by {commit.authorName}
            </Text>
          </Stack>
        </VersionDetailContent>
      </VersionDetailContainer>
    );
  };

  const publishedVersions = version?.Versions.filter((v) => v.versionType !== 'Draft') ?? [];
  const queryClient = useQueryClient();

  const refreshVersions = () => {
    queryClient.invalidateQueries(GitQueryKeys.Versions);
  };

  const columnWidth = '140px';

  return (
    <>
      <Stack direction='vertical' style={{ marginBottom: '48px' }} gap={theme.spaces.x24}>
        <Text level='md' tone={theme.colors.gray700} weight={theme.fontWeight.medium}>
          Published Versions
        </Text>

        <Table
          rowKey='commitHash'
          variant='secondary'
          virtualizeAfterHeight={publishedVersions?.length > 0 ? 145 : 240}
          loading={isInitialLoading}
          data-test-id='published-versions-table'
          columns={[
            {
              title: 'Release Version',
              dataIndex: 'version',
              key: 'version',
              width: columnWidth,
              resizable: false,
              render: (value) => (
                <VersionPill type='primary' level='xxs'>
                  <Ellipsis tooltip>{value || 'Draft'}</Ellipsis>
                </VersionPill>
              )
            },
            {
              title: 'Fabric(s)',
              dataIndex: 'fabrics',
              key: 'fabrics',
              width: '140px',
              resizable: false,
              render: (value) =>
                renderColumnWithEllipsis(value.map((fabric: { id: string; name: string }) => fabric.name).join(', '))
            },
            {
              title: 'Description',
              dataIndex: 'tagMessage',
              key: 'description',
              render: (value) => renderColumnWithEllipsis(value || 'No description', undefined, undefined, false)
            },
            {
              title: 'Published',
              dataIndex: 'created',
              key: 'created',
              width: columnWidth,
              resizable: false,
              render: (value) => (
                <Text level='xs' tone={theme.colors.gray700} weight={theme.fontWeight.normal}>
                  {timeAgoFormat(value)}
                </Text>
              )
            },
            {
              title: 'Last Run',
              dataIndex: 'lastDeploymentTime',
              key: 'lastDeploymentTime',
              width: columnWidth,
              resizable: false,
              render: (value) => (
                <Text level='xs' tone={theme.colors.gray700} weight={theme.fontWeight.normal}>
                  {value ? timeAgoFormat(value) : 'Not deployed'}
                </Text>
              )
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              width: columnWidth,
              resizable: false,
              render: (value) => {
                const status = simplifyVersionDeploymentStatus(value);
                return (
                  <>
                    {status.status ? (
                      <Badges size='s' variant='pillOutline' tone={status.color}>
                        {status.status}
                      </Badges>
                    ) : null}
                  </>
                );
              }
            }
          ]}
          dataSource={publishedVersions}
        />
      </Stack>
      {publishedVersions?.length > 0 && (
        <AllVersions>
          <Stack direction='vertical' gap={theme.spaces.x24}>
            <Text level='md' tone={theme.colors.gray700} weight={theme.fontWeight.medium}>
              All versions &nbsp;
              {isInitialLoading ? (
                <LoadingBIcon type='solid' size='xs' />
              ) : (
                <RefreshCwEIcon
                  type='solid'
                  size='xs'
                  cursor={'pointer'}
                  color={theme.colors.gray500}
                  onClick={refreshVersions}
                />
              )}
            </Text>

            <Stack direction='vertical'>
              {publishedVersions.map((v, index) => (
                <>
                  <VersionDetail
                    key={v.commitHash}
                    version={v}
                    isFirst={index === 0}
                    versionsList={publishedVersions}
                    versionIndex={index}
                    lastVersion={index === publishedVersions.length - 1 && !expandedVersionsIndex.includes(index)}
                    toggleVersion={toggleVersion}
                  />
                  {expandedVersionsIndex?.includes(index) &&
                    v.commits.map((c, idx) => (
                      <CommitDetail
                        key={c.commitHash}
                        commit={c}
                        isInitialCommit={index === publishedVersions.length - 1 && idx === v.commits.length - 1}
                        isLastCommit={idx === v.commits.length - 1}
                        publishedVersions={publishedVersions}
                        versionIndex={index}
                        currentCommitIndex={idx}
                      />
                    ))}
                </>
              ))}
            </Stack>
          </Stack>
        </AllVersions>
      )}
    </>
  );
};
